import React, { ComponentType, ReactNode, useMemo } from 'react'
import {
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import styled, { css } from 'styled-components'

import { neutralScale } from '../../theme'
import { semanticColors } from '../../theme/tokens'
import { ChartTooltipBody } from '../chart-tooltip'
import {
  TimeseriesChartAxis,
  TimeseriesChartProps,
} from './timeseries-chart.types'

const ChartContainer = styled.div`
  & .recharts-wrapper,
  & .recharts-surface,
  & path,
  & g {
    outline: none;
  }
`

/** Fallback series colour, shared by every chart kind. */
export const BASE_COLOR = 'hsl(263, 100%, 65%)'

// Recharts ties the cartesian grid (and the default series scale) to the Y-axis
// whose id is the default `0`. Keep the left axis on that default so the grid's
// horizontal lines render, and put the secondary axis on an explicit id.
const LEFT_AXIS_ID = 0
const RIGHT_AXIS_ID = 'right'

const TICK_STYLE = { fontSize: 12, fill: semanticColors.contentTertiary }
const NUMERIC_TICK_STYLE = {
  fontSize: 12,
  fill: semanticColors.contentDisabled,
}

// A y-axis reserves enough width for its ticks; a labelled one also needs room for
// the rotated caption printed beside them.
const AXIS_WIDTH_PX = 56
const LABELLED_AXIS_WIDTH_PX = 76

const AXIS_LABEL_STYLE = {
  fontSize: 12,
  fill: semanticColors.contentTertiary,
  textAnchor: 'middle' as const,
}

// Rotated to read along the axis it captions, mirrored per side so both labels
// read bottom-to-top on the left and top-to-bottom on the right.
const axisLabel = (value: string | undefined, side: 'left' | 'right') =>
  value === undefined
    ? undefined
    : {
        value,
        angle: side === 'left' ? -90 : 90,
        position:
          side === 'left' ? ('insideLeft' as const) : ('insideRight' as const),
        style: AXIS_LABEL_STYLE,
      }

// The x-axis reads left-to-right, so its caption stays upright on a line of its own
// beneath the ticks — which means the axis band has to grow by that line.
// `DEFAULT_X_AXIS_HEIGHT_PX` mirrors recharts' own default XAxis height.
const DEFAULT_X_AXIS_HEIGHT_PX = 30
const X_AXIS_LABEL_HEIGHT_PX = 20

const xAxisLabel = (value: string | undefined) =>
  value === undefined
    ? undefined
    : {
        value,
        position: 'insideBottom' as const,
        style: AXIS_LABEL_STYLE,
      }

const Row = styled.div<{ $position: 'side' | 'bottom' | 'auto' }>`
  display: flex;
  ${({ $position, theme }) =>
    $position === 'side'
      ? css`
          flex-direction: row;
          gap: ${theme.sizes.l};
        `
      : css`
          flex-direction: column;
          gap: ${theme.sizes.s};
        `}
`

// Structural shape of a recharts cartesian wrapper (`LineChart` / `AreaChart`) —
// only the props the frame passes. Narrower than recharts' own types, which vary
// awkwardly between the two, and enough to keep the call sites type-checked.
type CartesianChartComponent = ComponentType<{
  data?: Record<string, unknown>[]
  margin?: { top?: number; right?: number; bottom?: number; left?: number }
  children?: ReactNode
}>

export type TimeseriesChartFrameProps = TimeseriesChartProps & {
  /** The recharts cartesian wrapper hosting the marks (e.g. `LineChart`). */
  chart: CartesianChartComponent
  /** The marks — one element per series (e.g. a `<Line>` or `<Area>` each). */
  children: ReactNode
}

/**
 * The shell every timeseries chart shares: the responsive container, the
 * cartesian grid, the x-axis and one-or-two y-axes, the hover tooltip and the
 * optional legend. A chart kind supplies only its recharts wrapper and its marks,
 * so the line and area charts stay pixel-identical everywhere but the marks.
 *
 * Series may split across two y-axes — a `'right'` series draws against a
 * secondary axis that only mounts when something uses it — each axis formatted by
 * its own tick formatter.
 */
export const TimeseriesChartFrame = ({
  chart: Chart,
  children,
  data,
  dataKey,
  series,
  leftTickFormatter,
  rightTickFormatter,
  leftAxisLabel,
  rightAxisLabel,
  categoryAxisLabel,
  xTickFormatter,
  valueFormatter,
  totalKey,
  h = 240,
  w,
  showGrid = false,
  legend,
  legendPosition = 'bottom',
}: TimeseriesChartFrameProps) => {
  const hasRightAxis = useMemo(
    () => series.some((s) => s.axis === 'right'),
    [series]
  )

  const rendered = (
    <ChartContainer>
      <ResponsiveContainer width={w ?? '100%'} height={h}>
        <Chart
          data={data}
          margin={{ top: 8, right: hasRightAxis ? 8 : 0, bottom: 0, left: 0 }}
        >
          {showGrid && (
            <CartesianGrid
              vertical={false}
              stroke={neutralScale.neutral200}
              strokeDasharray="3 3"
            />
          )}
          <XAxis
            dataKey={dataKey}
            tickFormatter={xTickFormatter}
            tick={TICK_STYLE}
            label={xAxisLabel(categoryAxisLabel)}
            height={
              categoryAxisLabel
                ? DEFAULT_X_AXIS_HEIGHT_PX + X_AXIS_LABEL_HEIGHT_PX
                : undefined
            }
            axisLine={false}
            tickLine={false}
            minTickGap={24}
          />
          <YAxis
            yAxisId={LEFT_AXIS_ID}
            tickFormatter={leftTickFormatter}
            tick={NUMERIC_TICK_STYLE}
            label={axisLabel(leftAxisLabel, 'left')}
            axisLine={false}
            tickLine={false}
            width={leftAxisLabel ? LABELLED_AXIS_WIDTH_PX : AXIS_WIDTH_PX}
          />
          {hasRightAxis && (
            <YAxis
              yAxisId={RIGHT_AXIS_ID}
              orientation="right"
              tickFormatter={rightTickFormatter}
              tick={NUMERIC_TICK_STYLE}
              label={axisLabel(rightAxisLabel, 'right')}
              axisLine={false}
              tickLine={false}
              width={rightAxisLabel ? LABELLED_AXIS_WIDTH_PX : AXIS_WIDTH_PX}
            />
          )}
          <Tooltip
            content={
              <ChartTooltipBody
                valueFormatter={valueFormatter}
                labelFormatter={xTickFormatter}
                disableSort
                totalKey={totalKey}
              />
            }
            cursor={{ stroke: 'rgba(139, 74, 255, 0.2)' }}
          />
          {children}
        </Chart>
      </ResponsiveContainer>
    </ChartContainer>
  )

  if (!legend) return rendered

  return (
    <Row $position={legendPosition}>
      {rendered}
      {legend}
    </Row>
  )
}

/** The y-axis id a series' marks belong to. */
export const axisIdFor = (axis: TimeseriesChartAxis | undefined) =>
  axis === 'right' ? RIGHT_AXIS_ID : LEFT_AXIS_ID
